import { zodResolver } from '@hookform/resolvers/zod';
import { ShieldCheckIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { TagInput } from '@/components/TagInput';
import { ContactSourceBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useTenantSettings } from '@/features/org/queries';
import { errorMessage, isApiError } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { fmtDateTime } from '@/lib/format';
import type { ContactDto } from '@/lib/types';
import { cn } from '@/lib/utils';
import { useContactConsents, useCreateContact, useTags, useUpdateContact } from './queries';

const schema = z.object({
  firstName: z.string().trim().max(100),
  lastName: z.string().trim().max(100),
  phone: z.string().trim().min(3, 'Enter a mobile number').max(40),
  email: z.union([z.email('Enter a valid email'), z.literal('')]),
  company: z.string().trim().max(200),
  title: z.string().trim().max(200),
  language: z.string(),
  notes: z.string().trim().max(2000),
  tags: z.array(z.string()),
});
type Values = z.infer<typeof schema>;

const CONSENT_METHOD_LABEL: Record<string, string> = { sms_keyword: 'Texted the sign-up keyword', import: 'Declared on import', manual: 'Recorded by staff', web_form: 'Web form' };

/**
 * Add / edit form for one contact, shown in the app's right rail (a drawer on phones). `onDone` fires after a successful save with the saved contact
 * when the API returns it; `onCancel` closes whatever hosts the form.
 */
export function ContactEditor({
  jobId,
  contact,
  onDone,
  onCancel,
  className,
  autoFocus = true,
}: {
  jobId: string;
  contact?: ContactDto | null;
  onDone: (saved?: ContactDto) => void;
  onCancel: () => void;
  className?: string;
  autoFocus?: boolean;
}) {
  const me = useMe();
  const create = useCreateContact(jobId);
  const update = useUpdateContact(jobId);
  const tags = useTags(jobId);
  const settings = useTenantSettings(true);
  const consents = useContactConsents(jobId, contact ? contact.id : null);
  const [error, setError] = useState<string | null>(null);
  const [recordConsent, setRecordConsent] = useState(false);
  const [consentText, setConsentText] = useState('Agreed to receive job-site text alerts (onboarding paperwork on file).');
  const defaultLanguage = me.tenant.i18n.defaultLanguage;
  const languages = settings.data?.languages ?? [];
  const enabledLanguages = me.tenant.i18n.enabledLanguages;
  const empty: Values = { firstName: '', lastName: '', phone: '', email: '', company: '', title: '', language: defaultLanguage, notes: '', tags: [] };
  const toValues = (c: ContactDto): Values => ({ firstName: c.firstName, lastName: c.lastName, phone: c.phoneDisplay, email: c.email ?? '', company: c.company ?? '', title: c.title ?? '', language: c.language, notes: c.notes ?? '', tags: c.tags });
  // Seeded from the contact at mount (the rail keys this editor by contact id) so no field has to change value right after mounting.
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: contact ? toValues(contact) : empty });

  // Re-seed the form whenever a different contact (or "new") is shown.
  useEffect(() => {
    setError(null);
    setRecordConsent(false);
    form.reset(contact ? toValues(contact) : empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.id, contact?.updatedAt, form]);

  const onSubmit = form.handleSubmit(async (v) => {
    setError(null);
    const consent = recordConsent && consentText.trim() ? { method: 'manual' as const, text: consentText.trim() } : undefined;
    try {
      if (contact) {
        const r = await update.mutateAsync({ contactId: contact.id, body: { ...v, consent } });
        toast.success('Contact updated');
        onDone(r.contact);
      } else {
        const r = await create.mutateAsync({ ...v, consent });
        toast.success('Contact added');
        onDone(r.contact);
      }
    } catch (err) {
      if (isApiError(err) && err.status === 422) {
        for (const [k, msg] of Object.entries(err.fieldErrors())) form.setError(k as keyof Values, { message: msg });
      }
      setError(errorMessage(err));
    }
  });

  const busy = create.isPending || update.isPending;
  const showLanguage = me.tenant.features.translation || enabledLanguages.length > 1;
  const languageOptions = [...new Set([...enabledLanguages, contact?.language ?? defaultLanguage])];
  const dirty = form.formState.isDirty || recordConsent;

  return (
    <form onSubmit={onSubmit} className={cn('flex min-h-0 flex-1 flex-col', className)} noValidate>
      <div className="grid gap-4 p-4">
        <FormError message={error} />
        {contact && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <ContactSourceBadge source={contact.source} />
            <span className="text-muted-foreground">Added {fmtDateTime(contact.createdAt)}</span>
            {contact.optedOut && <span className="text-warning font-medium">Opted out</span>}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" htmlFor={`c-first-${contact?.id ?? 'new'}`} error={form.formState.errors.firstName?.message}>
            <Input id={`c-first-${contact?.id ?? 'new'}`} autoFocus={autoFocus} {...form.register('firstName')} />
          </Field>
          <Field label="Last name" htmlFor="c-last" error={form.formState.errors.lastName?.message}>
            <Input id="c-last" {...form.register('lastName')} />
          </Field>
        </div>
        <Field label="Mobile number" htmlFor="c-phone" required description="Any format; stored as +1XXXXXXXXXX." error={form.formState.errors.phone?.message}>
          <Input id="c-phone" type="tel" inputMode="tel" placeholder="(407) 555-0142" {...form.register('phone')} aria-invalid={!!form.formState.errors.phone} />
        </Field>
        <Field label="Email" htmlFor="c-email" error={form.formState.errors.email?.message}>
          <Input id="c-email" type="email" {...form.register('email')} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Company" htmlFor="c-company" error={form.formState.errors.company?.message}>
            <Input id="c-company" {...form.register('company')} />
          </Field>
          <Field label="Title / trade" htmlFor="c-title" error={form.formState.errors.title?.message}>
            <Input id="c-title" {...form.register('title')} />
          </Field>
        </div>
        {showLanguage && (
          <Field label="Preferred language" htmlFor="c-language" description="Texts are translated into it when translation is on." error={form.formState.errors.language?.message}>
            <Controller
              control={form.control}
              name="language"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="c-language" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languageOptions.map((code) => {
                      const l = languages.find((x) => x.code === code);
                      return (
                        <SelectItem key={code} value={code}>
                          {l ? `${l.name} · ${l.nativeName}` : code}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
        )}
        <Field label="Tags" htmlFor="c-tags" description="Use tags to message a group, e.g. electrical, night-shift." error={form.formState.errors.tags?.message}>
          <Controller control={form.control} name="tags" render={({ field }) => <TagInput id="c-tags" value={field.value} onChange={field.onChange} suggestions={(tags.data?.items ?? []).map((t) => t.tag)} />} />
        </Field>
        <Field label="Notes" htmlFor="c-notes" error={form.formState.errors.notes?.message}>
          <Textarea id="c-notes" rows={3} {...form.register('notes')} />
        </Field>

        <div className="grid gap-2 border p-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <ShieldCheckIcon className="size-4" /> Consent
          </div>
          {contact && (consents.data?.items.length ?? 0) > 0 ? (
            <ul className="grid gap-1.5 text-xs">
              {consents.data?.items.map((c) => (
                <li key={c.id} className="grid gap-0.5">
                  <span>
                    <span className="font-medium">{CONSENT_METHOD_LABEL[c.method] ?? c.method}</span>
                    {c.keyword && <span className="text-muted-foreground"> ({c.keyword})</span>} · <span className="text-muted-foreground">{fmtDateTime(c.createdAt)}</span>
                  </span>
                  {c.consentText && <span className="text-muted-foreground line-clamp-2">“{c.consentText}”</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground text-xs">{contact ? 'No consent record on file for this contact.' : 'Recipients must have agreed to receive texts at this number.'}</p>
          )}
          <label className="flex items-start gap-2 text-xs">
            <Checkbox checked={recordConsent} onCheckedChange={(v) => setRecordConsent(v === true)} className="mt-0.5" />
            <span>Record a consent entry now (e.g. signed onboarding paperwork)</span>
          </label>
          {recordConsent && <Textarea rows={2} value={consentText} onChange={(e) => setConsentText(e.target.value)} className="text-xs" maxLength={600} />}
        </div>
      </div>
      <div className="bg-card sticky bottom-0 mt-auto flex items-center justify-between gap-2 border-t p-4">
        <span className="text-muted-foreground text-xs">{dirty ? 'Unsaved changes' : ' '}</span>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            {contact ? 'Close' : 'Cancel'}
          </Button>
          <Button type="submit" disabled={busy || (!!contact && !dirty)}>
            {busy ? 'Saving…' : contact ? 'Save changes' : 'Add contact'}
          </Button>
        </div>
      </div>
    </form>
  );
}
